/**
 * NotificationToast Component
 *
 * Custom toast renderer for vendor real-time notifications.
 * Displayed via react-hot-toast when a new notification arrives over SSE.
 */

import { Box, Typography, IconButton } from '@mui/material';
import {
  Close as CloseIcon,
  EventAvailable as BookingIcon,
  Cancel as CancelIcon,
  CheckCircle as SuccessIcon,
  Error as ErrorIcon,
  AccountBalanceWallet as PayoutIcon,
} from '@mui/icons-material';
import { type Notification, NotificationType } from '@lov/shared';

interface NotificationToastProps {
  notification: Notification;
  onView: () => void;
  onDismiss: () => void;
}

type IconComponent = typeof BookingIcon;

const TOAST_ICON_MAP: Partial<Record<NotificationType, { icon: IconComponent; color: string }>> = {
  [NotificationType.VENDOR_NEW_BOOKING]: { icon: BookingIcon, color: '#4caf50' },
  [NotificationType.BOOKING_CANCELLED]: { icon: CancelIcon, color: '#f44336' },
  [NotificationType.PAYMENT_SUCCESS]: { icon: SuccessIcon, color: '#4caf50' },
  [NotificationType.PAYMENT_FAILED]: { icon: ErrorIcon, color: '#f44336' },
  [NotificationType.PAYOUT_SUCCESS]: { icon: PayoutIcon, color: '#4caf50' },
  [NotificationType.PAYOUT_FAILURE]: { icon: PayoutIcon, color: '#f44336' },
};

export function NotificationToast({ notification, onView, onDismiss }: NotificationToastProps) {
  const config = TOAST_ICON_MAP[notification.type] ?? { icon: BookingIcon, color: '#1976d2' };
  const Icon = config.icon;

  return (
    <Box
      onClick={onView}
      sx={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 1.5,
        p: 1.5,
        bgcolor: 'background.paper',
        borderRadius: 2,
        boxShadow: 3,
        cursor: 'pointer',
        minWidth: 300,
        maxWidth: 400,
        border: 1,
        borderColor: 'divider',
        '&:hover': { bgcolor: 'action.hover' },
      }}
    >
      <Icon sx={{ color: config.color, fontSize: 28, mt: 0.25 }} />
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="subtitle2" noWrap>
          {notification.title}
        </Typography>
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
          }}
        >
          {notification.body}
        </Typography>
      </Box>
      <IconButton
        size="small"
        aria-label="Dismiss notification"
        onClick={(e) => {
          e.stopPropagation();
          onDismiss();
        }}
        sx={{ mt: -0.5, mr: -0.5 }}
      >
        <CloseIcon fontSize="small" />
      </IconButton>
    </Box>
  );
}
